import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { findPostBySlug, BLOG_POSTS, BLOG_CATEGORIES } from "@/content/blog";
import { renderMarkdown } from "@/lib/markdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? findPostBySlug(slug) : undefined;
  if (!post) return <Navigate to="/blog" replace />;

  const html = renderMarkdown(post.body);
  const url = `https://clickandgard.fr/blog/${post.slug}`;
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);

  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt || post.publishedAt,
      author: { "@type": "Organization", name: "Click&Gard" },
      publisher: {
        "@type": "Organization",
        name: "Click&Gard",
        logo: { "@type": "ImageObject", url: "https://clickandgard.fr/favicon.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://clickandgard.fr/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://clickandgard.fr/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    },
  ];

  if (post.faq && post.faq.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faq.map((q) => ({
        "@type": "Question",
        name: q.question,
        acceptedAnswer: { "@type": "Answer", text: q.answer },
      })),
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="container mx-auto px-4 py-10 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour au blog
        </Link>

        <header className="mb-8 space-y-3">
          <div className="flex flex-wrap gap-2 items-center text-xs text-muted-foreground">
            <Badge variant="secondary">
              {BLOG_CATEGORIES.find((c) => c.id === post.category)?.label}
            </Badge>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(post.publishedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {post.readingMinutes} min de lecture
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold leading-tight">{post.title}</h1>
          <p className="text-lg text-muted-foreground">{post.description}</p>
        </header>

        <article
          className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-h2:mt-8 prose-h2:mb-3 prose-h3:mt-6 prose-table:text-sm"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {post.faq && post.faq.length > 0 && (
          <section className="mt-12 space-y-4">
            <h2 className="text-2xl font-display font-bold">Questions fréquentes</h2>
            {post.faq.map((q, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{q.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{q.answer}</p>
                </CardContent>
              </Card>
            ))}
          </section>
        )}

        <section className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border text-center">
          <h2 className="text-2xl font-display font-bold mb-2">Trouvez votre baby-sitter en quelques clics</h2>
          <p className="text-muted-foreground mb-4">
            Profils vérifiés, géolocalisés, disponibilités en temps réel.
          </p>
          <Button asChild size="lg">
            <Link to="/search">
              Voir les pros disponibles <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
        </section>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-display font-bold mb-4">À lire aussi</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group">
                  <Card className="h-full hover:shadow-md transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-sm group-hover:text-primary transition-colors line-clamp-3">
                        {r.title}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default BlogPost;
